import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type Immutable from 'immutable';

const useConfirmOptions = (paymentsIds: Immutable.Set<string>, selectedPayments: Immutable.Map<string, any>) => {
  const { formatMessage } = useIntl();

  const mergePaymentsData = useMemo(() => {
    const result = selectedPayments.reduce(
      (acc: { vendorName: string; currency: string; count: number; summCents: number }, item: any) => {
        acc.vendorName = item?.vendorName;
        acc.currency = item?.transferCurrency;
        acc.summCents += Math.round(parseFloat(item.totalAmount) * 100);
        return acc;
      },
      { vendorName: '', currency: '', count: paymentsIds.size, summCents: 0 },
    );
    return {
      vendorName: result.vendorName,
      currency: result.currency,
      count: result.count,
      totalAmount: (result.summCents / 100).toFixed(2),
    };
  }, [paymentsIds.size, selectedPayments]);

  return useMemo(
    () => ({
      mergePayments: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.mergePayments.title',
          defaultMessage: 'Merge payments?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.mergePayments.placeholder',
            defaultMessage: `${mergePaymentsData.count} payment(s) from ${mergePaymentsData.vendorName} in ${mergePaymentsData.currency} will be merged into one payment of ${mergePaymentsData.totalAmount}.`,
          },
          mergePaymentsData,
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.mergePayments.btn',
          defaultMessage: 'Merge',
        }),
      },
      canNotMergePayments: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.canNotMergePayments.title',
          defaultMessage: "Can't merge these payments",
        }),
        description: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.canNotMergePayments.placeholder',
          defaultMessage: 'To merge payments, select items from the same vendor and currency.',
        }),
        confirmationText: formatMessage({
          id: 'button.ok',
          defaultMessage: 'Ok',
        }),
        hideCancelButton: true,
      },
      sendToApproval: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.sendToApproval.title',
          defaultMessage: 'Send to approval?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.sendToApproval.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be send to approval.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.sendToApproval.btn',
          defaultMessage: 'Send to approval',
        }),
      },
      startPaymentProcess: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.startPaymentProcess.title',
          defaultMessage: 'Start payment process?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.startPaymentProcess.placeholder',
            defaultMessage: `The payment process for ${paymentsIds.size} payment(s) will start.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.startPaymentProcess.btn',
          defaultMessage: 'Start payment process',
        }),
      },
      saveForLater: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.saveForLater.title',
          defaultMessage: 'Save for later?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.saveForLater.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be saved for later.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.saveForLater.btn',
          defaultMessage: 'Save for later',
        }),
      },
      archive: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.archive.title',
          defaultMessage: 'Archive?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.archive.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be archived.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.archive.btn',
          defaultMessage: 'Archive',
        }),
      },
      unarchive: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.unarchive.title',
          defaultMessage: 'Unarchive?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.unarchive.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be unarchived.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.unarchive.btn',
          defaultMessage: 'Unarchive',
        }),
      },
      approve: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.approve.title',
          defaultMessage: 'Approve?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.approve.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be approved.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.approve.btn',
          defaultMessage: 'Approve',
        }),
      },
      reject: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.reject.title',
          defaultMessage: 'Reject?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.reject.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be rejected.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.reject.btn',
          defaultMessage: 'Reject',
        }),
      },
      resetApprovalFlow: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.resetApprovalFlow.title',
          defaultMessage: 'Reset approval flow?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.resetApprovalFlow.placeholder',
            defaultMessage: `The approval flow will be reset for ${paymentsIds.size} payment(s).`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.resetApprovalFlow.btn',
          defaultMessage: 'Reset',
        }),
      },
      pay: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.pay.title',
          defaultMessage: 'Pay?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.pay.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be paid for a total of ${mergePaymentsData.totalAmount} ${mergePaymentsData.currency}.`,
          },
          { count: paymentsIds.size, totalAmount: mergePaymentsData.totalAmount, currency: mergePaymentsData.currency },
        ),
        confirmationText: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.pay.btn',
            defaultMessage: `Pay ${mergePaymentsData.totalAmount}`,
          },
          { totalAmount: mergePaymentsData.totalAmount },
        ),
      },
      deletePayments: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.deletePayments.title',
          defaultMessage: 'Delete payments?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.deletePayments.placeholder',
            defaultMessage: '{count} payment(s) will be deleted.',
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'button.delete',
          defaultMessage: 'Delete',
        }),
        confirmationButtonProps: {
          color: 'error' as const,
        },
      },
    }),
    [formatMessage, mergePaymentsData, paymentsIds.size],
  );
};

export default useConfirmOptions;
